import React from 'react';
import { Datagrid } from 'react-admin';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { ColumnConfig } from '../hooks/useColumnReorder';

interface DraggableDatagridProps {
    columns: ColumnConfig[];
    onColumnReorder: (sourceIndex: number, destinationIndex: number) => void;
    children: React.ReactNode;
    [key: string]: any; // For other Datagrid props
}

/**
 * A custom Datagrid component that supports column reordering via drag and drop
 */
const DraggableDatagrid: React.FC<DraggableDatagridProps> = ({
    columns,
    onColumnReorder,
    children,
    ...rest
}) => {
    const [highlightedColumn, setHighlightedColumn] = React.useState<
        number | null
    >(null);

    const handleHeaderDragEnd = (result: any) => {
        if (!result.destination) return;

        // Find the column IDs by their positions
        const visibleColumns = columns.filter(col => col.visible);
        const sourceColumn = visibleColumns[result.source.index];
        const destinationIndex = result.destination.index;

        // Find the actual indices in the full columns array
        const sourceIndex = columns.findIndex(
            col => col.id === sourceColumn.id
        );
        const destinationColumn = visibleColumns[destinationIndex];
        const targetIndex = columns.findIndex(
            col => col.id === destinationColumn.id
        );

        // Show visual feedback using React state instead of direct DOM manipulation
        setHighlightedColumn(destinationIndex);
        setTimeout(() => {
            setHighlightedColumn(null);
        }, 500);

        onColumnReorder(sourceIndex, targetIndex);
    };

    return (
        <DragDropContext onDragEnd={handleHeaderDragEnd}>
            <Droppable droppableId="columns-header" direction="horizontal">
                {provided => (
                    <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        style={{ display: 'contents' }}
                    >
                        <Datagrid
                            headerStyle={field => {
                                // Find the column index
                                const visibleColumns = columns.filter(
                                    col => col.visible
                                );
                                const columnIndex = visibleColumns.findIndex(
                                    col => col.id === field
                                );
                                if (columnIndex === -1) return {};

                                // Apply highlight style if this is the column that was just moved
                                const isHighlighted =
                                    highlightedColumn === columnIndex;

                                return {
                                    position: 'relative',
                                    cursor: 'grab',
                                    backgroundColor: isHighlighted
                                        ? '#e3f2fd'
                                        : undefined,
                                    transition: 'background-color 0.3s',
                                    '&:hover': {
                                        backgroundColor: '#f5f5f5',
                                    },
                                    '&:hover .drag-handle': {
                                        opacity: 1,
                                    },
                                };
                            }}
                            header={
                                <div style={{ display: 'flex', width: '100%' }}>
                                    {columns
                                        .filter(col => col.visible)
                                        .map((column, index) => (
                                            <Draggable
                                                key={column.id}
                                                draggableId={`header-${column.id}`}
                                                index={index}
                                            >
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        className={`column-header ${
                                                            snapshot.isDragging
                                                                ? 'dragging'
                                                                : ''
                                                        }`}
                                                        style={{
                                                            ...provided
                                                                .draggableProps
                                                                .style,
                                                            display: 'none', // Hide the draggable elements as they're just for state management
                                                        }}
                                                    />
                                                )}
                                            </Draggable>
                                        ))}
                                    {provided.placeholder}
                                </div>
                            }
                            {...rest}
                        >
                            {children}
                        </Datagrid>
                    </div>
                )}
            </Droppable>
        </DragDropContext>
    );
};

// Create a style component for the draggable datagrid
const DraggableDatagridStyles = () => {
    React.useEffect(() => {
        const customStyles = `
    .column-header.dragging {
      background-color: #e0e0e0;
      box-shadow: 0 5px 10px rgba(0,0,0,0.2);
    }
    
    .MuiTableCell-head {
      position: relative;
    }
    
    .MuiTableCell-head:hover::before {
      content: '⋮⋮';
      position: absolute;
      left: 4px;
      top: 50%;
      transform: translateY(-50%);
      font-size: 14px;
      color: #757575;
      cursor: grab;
    }
    
    .MuiTableCell-head:hover {
      background-color: #f5f5f5;
    }
    
    .MuiTableCell-head.dragging {
      background-color: #e0e0e0;
      opacity: 0.8;
    }
    
    /* Add touch support */
    @media (pointer: coarse) {
      .MuiTableCell-head::before {
        content: '⋮⋮';
        position: absolute;
        left: 4px;
        top: 50%;
        transform: translateY(-50%);
        font-size: 14px;
        color: #757575;
        opacity: 0.7;
      }
    }
    `;

        // Check if styles are already added
        if (!document.getElementById('draggable-datagrid-styles')) {
            const styleElement = document.createElement('style');
            styleElement.id = 'draggable-datagrid-styles';
            styleElement.innerHTML = customStyles;
            document.head.appendChild(styleElement);
        }

        // Clean up function to remove styles when component unmounts
        return () => {
            const styleElement = document.getElementById(
                'draggable-datagrid-styles'
            );
            if (styleElement && styleElement.parentNode) {
                styleElement.parentNode.removeChild(styleElement);
            }
        };
    }, []);

    return null;
};

// Wrapper component that includes both the styles and the datagrid
const DraggableDatagridWithStyles: React.FC<DraggableDatagridProps> = props => {
    return (
        <>
            <DraggableDatagridStyles />
            <DraggableDatagrid {...props} />
        </>
    );
};

// Export the wrapper component as the default
export default DraggableDatagridWithStyles;
